import type { JoinResponse } from '@biplanes/contracts';
import type { FlightClient } from '../wasm/biplanes_client.js';
export type ArenaClient = FlightClient;
async function post<T>(
  path: string,
  body: unknown,
  signal: AbortSignal,
  token?: string,
): Promise<T> {
  const response = await fetch(path, {
    method: 'POST',
    signal,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const value: unknown = await response.json();
  if (!response.ok) {
    const error =
      value && typeof value === 'object' && 'error' in value
        ? String(value.error)
        : 'Arena request failed';
    throw new Error(error);
  }
  return value as T;
}
let modulePromise: Promise<typeof import('../wasm/biplanes_client.js')> | undefined;
export async function joinArena(
  nickname: string,
  pilot: number,
  signal: AbortSignal,
): Promise<ArenaClient> {
  if (!('WebTransport' in globalThis))
    throw new Error('This browser needs WebTransport support to join an arena');
  const session = await post<{ token: string }>('/api/session', { nickname, pilot }, signal);
  const join = await post<JoinResponse>('/api/join', { mode: 'ffa' }, signal, session.token);
  if (join.version !== 1 || join.mode !== 'ffa' || join.stepMs !== 50)
    throw new Error('Incompatible arena protocol');
  modulePromise ??= import('../wasm/biplanes_client.js').then(async (module) => {
    await module.default();
    return module;
  });
  const module = await modulePromise;
  signal.throwIfAborted();
  return new module.FlightClient(join.endpoint, join.certificateHash, join.ticket, join.player);
}
